if (!customElements.get('flipzy-step-video')) {
  customElements.define('flipzy-step-video', class extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      this.video = this.querySelector('video');
      if (!this.video) return;

      this.poster = this.querySelector('.flipzy-video-steps__poster');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      this.visible = false;
      this.loaded = false;
      this.failed = false;
      this.disconnected = false;
      this.starting = false;
      this.attempt = (this.attempt || 0) + 1;
      this.video.muted = this.video.defaultMuted = true;
      this.video.controls = false;

      const listeners = [];
      const listen = (target, type, handler) => {
        if (!target?.addEventListener) return;
        target.addEventListener(type, handler);
        listeners.push(() => target.removeEventListener(type, handler));
      };
      const update = () => this.update();
      listen(this.poster, 'load', update);
      listen(this.poster, 'error', update);
      listen(this.motion, 'change', update);
      listen(this.connection, 'change', update);
      listen(window, 'online', update);
      listen(window, 'offline', update);
      listen(document, 'visibilitychange', update);
      listen(this.video, 'playing', () => {
        if (!this.canPlay()) {
          this.suspend();
          return;
        }
        this.starting = false;
        this.clearDeadline();
        this.setAttribute('data-playing', '');
      });
      listen(this.video, 'waiting', () => {
        this.removeAttribute('data-playing');
        if (this.loaded && this.canPlay() && !this.deadline) {
          this.deadline = setTimeout(() => this.fallback(), 3500);
        }
      });
      listen(this.video, 'error', () => {
        if (this.loaded) this.fallback();
      });

      let observer;
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting && entry.intersectionRatio >= 0.15;
          this.update();
        }, { threshold: [0, 0.15] });
        observer.observe(this);
      }

      this.cleanup = () => {
        this.disconnected = true;
        observer?.disconnect();
        listeners.forEach(remove => remove());
        this.unload();
      };
    }

    constrained() {
      const connection = this.connection;
      return this.motion.matches || navigator.onLine === false || connection?.saveData ||
        ['slow-2g', '2g'].includes(connection?.effectiveType) ||
        (typeof connection?.downlink === 'number' && connection.downlink < 0.8);
    }

    canPlay() {
      return !this.disconnected && !this.failed && this.visible && !document.hidden && !this.constrained();
    }

    chooseSource() {
      const candidates = [...this.video.querySelectorAll('source[data-src]')].filter(source =>
        this.video.canPlayType(source.type)
      );
      const sized = candidates.filter(source => Number(source.dataset.width) > 0);
      if (sized.length) {
        sized.sort((a, b) => Number(a.dataset.width) - Number(b.dataset.width));
        return (sized.filter(source => Number(source.dataset.width) <= 768).pop() || sized[0]).dataset.src;
      }
      return candidates[0]?.dataset.src;
    }

    update() {
      if (this.disconnected) return;
      if (this.constrained()) {
        this.unload();
        return;
      }
      if (!this.canPlay()) {
        this.suspend();
        return;
      }
      if (this.poster && !this.poster.complete) return;
      if (this.starting || !this.video.paused) return;
      this.starting = true;
      this.paintFrame = requestAnimationFrame(() => {
        this.paintFrame = requestAnimationFrame(() => this.start());
      });
    }

    start() {
      this.paintFrame = null;
      if (!this.canPlay()) {
        this.suspend();
        return;
      }
      if (!this.loaded) {
        const source = this.chooseSource();
        if (!source) {
          this.fallback();
          return;
        }
        this.loaded = true;
        this.video.preload = 'auto';
        this.video.src = source;
        this.video.load();
      }
      const attempt = ++this.attempt;
      this.clearDeadline();
      this.deadline = setTimeout(() => this.fallback(), 6500);
      try {
        this.video.play()?.catch(() => {
          if (attempt === this.attempt && this.canPlay()) this.fallback();
        });
      } catch (_) {
        this.fallback();
      }
    }

    clearDeadline() {
      clearTimeout(this.deadline);
      this.deadline = null;
    }

    suspend() {
      ++this.attempt;
      this.starting = false;
      cancelAnimationFrame(this.paintFrame);
      this.paintFrame = null;
      this.clearDeadline();
      this.video.pause();
      this.removeAttribute('data-playing');
    }

    unload() {
      this.suspend();
      if (!this.loaded) return;
      this.loaded = false;
      this.video.removeAttribute('src');
      this.video.preload = 'none';
      this.video.load();
    }

    fallback() {
      this.failed = true;
      this.unload();
    }

    disconnectedCallback() {
      this.cleanup?.();
      this.cleanup = null;
    }
  });
}
